import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../../../shared/components/Sidebar";
import PageLayout from "../../../shared/components/PageLayout";
import "./Admin.css";
import { DataGrid } from "@mui/x-data-grid";
import {
  collection,
  doc,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { useFirebase } from "../../../context/Firebase";
import {
  Button,
  CircularProgress,
  Snackbar,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, Edit, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import BackButton from "../../../shared/components/Backbtn";

const Admin = () => {
  const { db } = useFirebase();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: "" });
  const [sortModel, setSortModel] = useState([
    { field: "serialNumber", sort: "asc" },
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = [];
      let serial = 1;
      const querySnapshot = await getDocs(collection(db, "users"));
      querySnapshot.forEach((docSnap) => {
        const userData = docSnap.data();
        list.push({ id: docSnap.id, serialNumber: serial++, ...userData });
      });
      setData(list);
      setFilteredData(list);
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteDoc(doc(db, "users", id));
        setData((prev) => prev.filter((user) => user.id !== id));
        setFilteredData((prev) => prev.filter((user) => user.id !== id));
        setDeleteDialog({ open: false, id: null, name: "" });
      } catch {
        setError("Failed to delete user.");
      }
    },
    [db]
  );

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    setFilteredData(
      data.filter((user) => user.name?.toLowerCase().includes(value))
    );
  };

  const columns = [
    { field: "serialNumber", headerName: "#", width: 70, sortable: true },
    { field: "name", headerName: "User Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.row.isBlocked ? "Blocked" : "Active"}
          color={params.row.isBlocked ? "error" : "success"}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <>
          <Tooltip title="View">
            <IconButton sx={{ color: '#757575' }} onClick={() => navigate(`/user/${params.row.id}`)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton color="secondary" onClick={() => navigate(`/user/${params.row.id}?edit=true`)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={() => setDeleteDialog({ open: true, id: params.row.id, name: params.row.name })}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  if (loading)
    return (
      <div className="centered">
        <CircularProgress />
      </div>
    );

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content">
        <Snackbar open={!!error} autoHideDuration={6000} message={error} onClose={() => setError(null)} />
        <PageLayout
          title="User List"
          actions={
            <>
              <BackButton tooltip="Back" />
              <Button variant="contained" color="primary" onClick={() => navigate("/add-user")}>
                Add New User
              </Button>
            </>
          }
        >
          <TextField
            label="Search Users"
            fullWidth
            value={searchTerm}
            onChange={handleSearch}
            sx={{ mb: 2, borderRadius: 1 }}
          />
          <div className="data-grid-container">
            <div className="data-grid-wrapper">
              <DataGrid
                rows={filteredData}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: 40 } },
                }}
                pageSizeOptions={[20, 40, 100]}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                disableSelectionOnClick
                sx={{ height: '100%', borderRadius: 2 }}
              />
            </div>
          </div>
        </PageLayout>

        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, name: "" })}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null, name: "" })}>Cancel</Button>
            <Button color="error" variant="contained" onClick={() => handleDelete(deleteDialog.id)}>Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
